import { supabase } from '../supabase';

export const GOOGLE_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbwY3RrYla4Mz-XN5YJjbPuOoyqcKOeJzzuNbBRonjT0e9OCSQY_R0kY5qSbkT_M9aplsA/exec";

/**
 * Uploads a file to Google Drive via the Apps Script Web App.
 * Returns the public view URL of the uploaded file.
 */
export async function uploadToGoogleDrive(file: File, folderId?: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async () => {
      try {
        const base64Data = (reader.result as string).split(',')[1];

        // We use 'no-cors' mode initially usually, but to get a response we need regular mode.
        // Google Apps Script requires sending data as text/plain to avoid CORS preflight (OPTIONS) 
        // issues if the script doesn't explicitly handle them.
        const payload = {
          file: base64Data,
          filename: file.name,
          mimeType: file.type,
          folderId: folderId // Optional, if we want to support subfolders later
        };

        const response = await fetch(GOOGLE_SCRIPT_URL, {
          method: 'POST',
          mode: 'cors', // We need the response
          headers: {
            'Content-Type': 'text/plain;charset=utf-8', // Bypass CORS preflight
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          throw new Error(`Upload failed with status: ${response.status}`);
        }

        const data = await response.json();

        if (data.status === 'success') {
          resolve(data.url);
        } else {
          reject(new Error(data.message || 'Unknown upload error'));
        }
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
}

/**
 * Uploads a file to Supabase Storage.
 * @param file - The file to upload
 * @param bucket - The Supabase storage bucket name (e.g. 'teacher-images' or 'teacher-pdfs')
 * @param ownerId - The authenticated user id that owns the uploaded file
 * @param folder - Optional subfolder within the user's folder inside the bucket
 * @returns The public URL of the uploaded file.
 */
export async function uploadToSupabaseStorage(file: File, bucket: string, ownerId: string, folder = ''): Promise<string> {
  if (!ownerId || ownerId === 'pending') {
    throw new Error('Create your account first, then upload files from your dashboard.');
  }

  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError || !user) {
    throw new Error('Please sign in before uploading files.');
  }

  if (user.id !== ownerId) {
    throw new Error('You can only upload files for your own account.');
  }

  // Sanitize file name: replace non-alphanumeric/dot/dash characters with underscore
  // to avoid "Invalid key" errors from Supabase Storage for brackets, etc.
  const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');

  const userFolder = folder
    ? `${ownerId}/${folder}`
    : `${ownerId}`;

  const filePath = `${userFolder}/${Date.now()}_${sanitizedName}`;

  const { error } = await supabase.storage.from(bucket).upload(filePath, file, { upsert: true });
  if (error) throw new Error('Storage upload failed: ' + error.message);

  const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
  return data.publicUrl;
}

/**
 * Converts a Google Drive View URL to a Direct Image Link.
 * View URL: https://drive.google.com/file/d/FILE_ID/view...
 * Direct URL: https://lh3.googleusercontent.com/d/FILE_ID
 */
export function getGoogleDriveDirectLink(url: string): string {
  if (!url) return url;

  // If it's a Firebase Storage URL or any non-Google-Drive URL, return as-is
  if (!url.includes('drive.google.com') && !url.includes('docs.google.com')) {
    return url;
  }

  // Extract ID
  // Supports formats:
  // - https://drive.google.com/file/d/ABC123/view
  // - https://drive.google.com/open?id=ABC123

  let id = '';
  const parts = url.split('/');

  // Check for /file/d/{id} format
  const dIndex = parts.indexOf('d');
  if (dIndex !== -1 && dIndex < parts.length - 1) {
    id = parts[dIndex + 1];
  }

  // Check for id= param (less common for view links but good to support)
  if (!id && url.includes('id=')) {
    const match = url.match(/id=([^&]+)/);
    if (match) id = match[1];
  }

  if (id) {
    // Using thumbnail API bypasses the 403 Forbidden issue caused by recent Google Drive policy changes
    return `https://drive.google.com/thumbnail?id=${id}&sz=w800`;
  }

  return url;
}
